import { httpClient } from "infra/axios/axios-api";
import { DigitalSignApprover } from "domain/entities/digital-sign-approver";
import { Tripsheet } from "domain/entities/tripsheet";
import { DigitalSignStepEnum } from "domain/enum/digitalSignSteps";
import { MessageTypeEnum } from "domain/enum/messageType";
import { TripsheetStatusEnum } from "domain/enum/tripsheetStatus";
import { translateText } from "infra/translation/translate";
import Spinner from "presentation/components/Shared/Spinner";
import { useDigitalSignApproverByFilter } from "presentation/hooks/use-query-digital-sign-approver";
import { useTripsheetsByFilter } from "presentation/hooks/use-query-tripsheet";
import { showToastMessage } from "presentation/services/toastService";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import React, { useEffect, useRef, useState } from "react";
import { downloadSignedTripsheetPdfsAsZip } from "presentation/utils/exportUtils";

/**
 * Formata o status do mapa de KMs para exibição ao usuário.
 * @param {string} status - O status atual do mapa de KMs.
 * @returns Uma string formatada representando o status.
 */
const formatStatus = (status: string): string => {
  if (status === TripsheetStatusEnum.ASSINADO_2) return "Assinado";
  if (status === TripsheetStatusEnum.ASSINADO_1) return "Pendente";
  return status;
};

/**
 * @overview
 * O `TripsheetDocumentsGrid` é um componente que exibe uma grelha com os documentos
 * de mapa de KMs assinados (status "Assinado 1" ou "Assinado 2") para o utilizador logado.
 *
 * @description
 * Este componente é responsável por:
 * - Buscar e listar os mapas de KMs relevantes usando o hook `useTripsheetsByFilter`.
 * - Permitir que o utilizador visualize o relatório em PDF de um mapa de KMs específico.
 * - Gerir o estado de carregamento e de erro durante a busca de dados.
 * - Apresentar notificações (toasts) para feedback ao utilizador.
 */
const TripsheetDocumentsGrid: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { userEmployee } = useAuthStore();
  const [tripsheetIdToReport, setTripsheetIdToReport] = useState<string | null>(
    null
  );

  const { data: digitalSignApproverData, isFetching: isFetchingApprover } =
    useDigitalSignApproverByFilter({
      referenceId: tripsheetIdToReport ?? "",
      enabled: String(!!tripsheetIdToReport),
    });

  const {
    data: tripsheetsData,
    isError,
    isLoading,
    error,
  } = useTripsheetsByFilter({
    employeeId: userEmployee?.id ?? "",
    status: "Assinado 1,Assinado 2",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  useEffect(() => {
    // Este efeito reage à chegada dos dados da assinatura digital
    if (digitalSignApproverData?.data) {
      if (digitalSignApproverData.data.length > 0) {
        getSignedTripsheet(digitalSignApproverData.data);
      } else if (!isFetchingApprover) {
        // Garante que a mensagem só aparece depois que a busca termina sem resultados
        showToastMessage(
          toast,
          MessageTypeEnum.WARN,
          "Relatório",
          "Ainda não existe um relatório para este mapa de KMs."
        );
      }
    }
  }, [digitalSignApproverData, isFetchingApprover]);

  if (isLoading) return <Spinner />;

  if (isError) return <p>Ocorreu um erro: {error.message}</p>;

  /**
   * Renderiza as opções de ação para cada linha da tabela.
   * Atualmente, exibe um ícone para visualizar o PDF se o documento tiver um status apropriado.
   * @param {Tripsheet} rowData - Os dados da linha atual da tabela.
   * @returns {JSX.Element} O JSX para a coluna de ações.
   */
  const showActionOptions = (rowData: Tripsheet) => {
    const hasDocument = [
      TripsheetStatusEnum.A_ASSINAR,
      TripsheetStatusEnum.ASSINADO_1,
      TripsheetStatusEnum.ASSINADO_2,
    ].includes(rowData.status as TripsheetStatusEnum);

    return (
      <div>
        {hasDocument && (
          <i
            className="pi pi-file-pdf cursor-pointer"
            title="Exibir relatório"
            onClick={() => rowData.id && setTripsheetIdToReport(rowData.id)}
          />
        )}
      </div>
    );
  };

  /**
   * Obtém o URL pré-assinado do S3 para o relatório em PDF e abre-o numa nova aba.
   * Prioriza o documento assinado pelo funcionário, depois o assinado pela empresa,
   * e por fim, o documento original.
   * @param {DigitalSignApprover[]} data - Um array com os dados dos aprovadores da assinatura digital.
   * @async
   * @returns {Promise<void>}
   */
  const getSignedTripsheet = async (data: DigitalSignApprover[]) => {
    try {
      const docSignedByCompany = data.find(
        (item) => item.step === DigitalSignStepEnum.STEP_COMPANY
      );
      const docSignedByEmployee = data.find(
        (item) => item.step === DigitalSignStepEnum.STEP_EMPLOYEE
      );

      const s3Path =
        docSignedByEmployee?.documentPath || docSignedByCompany?.documentPath;

      if (!s3Path) {
        throw new Error("Nenhum caminho de documento encontrado.");
      }

      const signedUrlResponse = await httpClient.get(
        `/assets/${s3Path}`
      );
      const signedUrl = signedUrlResponse.data.url;

      if (!signedUrl) {
        throw new Error("A API não retornou uma URL para o relatório.");
      }
      window.open(signedUrl, "_blank");
    } catch (err) {
      showToastMessage(
        toast,
        MessageTypeEnum.ERROR,
        "Relatório",
        "Ocorreu um erro ao buscar o relatório. Por favor, tente novamente."
      );
    } finally {
      // Reseta o ID aqui, garantindo que a operação terminou.
      setTripsheetIdToReport(null);
    }
  };

  return (
    <div>
      <DataTable
        value={tripsheetsData?.data || []}
        size="small"
        loading={isLoading}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        resizableColumns
        columnResizeMode="expand"
        dataKey="id"
      >
        <Column field="year" header={translateText("Ano", "pt")} />
        <Column field="month" header={translateText("Mês", "pt")} />
        <Column
          field="status"
          header={translateText("Estado", "pt")}
          body={(rowData: Tripsheet) => formatStatus(rowData.status)}
        />
        <Column
          body={showActionOptions}
          header={translateText("Ações", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default TripsheetDocumentsGrid;
